import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Button,
} from "@mui/material";
import "@fontsource/rethink-sans";
import { Wordmark } from "@trusty/shared";

const binaries: Record<string, Record<string, string>> = {
  macOS: {
    x86: "https://storage.googleapis.com/trusty-public-app-updates/trusty-desktop-app/Trusty-x64.dmg",
    arm: "https://storage.googleapis.com/trusty-public-app-updates/trusty-desktop-app/Trusty-arm64.dmg",
  },
  Windows: {
    x86: "https://storage.googleapis.com/trusty-public-app-updates/Trusty%20Browser-2.1.3%20Setup.exe",
    arm: "https://storage.googleapis.com/trusty-public-app-updates/Trusty%20Browser-2.1.3%20Setup.exe",
  },
};

const MacOSIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const WindowsIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 48 48"
  >
    <path fill="#f44f5a" d="m22,22H6V6h16v16Z"></path>
    <path fill="#21ad64" d="m42,22h-16V6h16v16Z"></path>
    <path fill="#fede00" d="m42,42h-16v-16h16v16Z"></path>
    <path fill="#33bef0" d="m22,42H6v-16h16v16Z"></path>
  </svg>
);

const platformIcons: Record<string, React.ReactNode> = {
  macOS: <MacOSIcon />,
  Windows: <WindowsIcon />,
};

const StyledButton: React.FC<React.ComponentProps<typeof Button>> = (props) => (
  <Button
    {...props}
    sx={{
      fontFamily: "'Rethink Sans', sans-serif",
      fontWeight: 600,
      fontSize: "16px",
      padding: "10px 20px",
      backgroundColor: "rgba(229, 19, 90, 1)",
      color: "#fff",
      ":hover": {
        backgroundColor: "rgba(200, 10, 70, 1)",
      },
    }}
  >
    {props.children}
  </Button>
);

const App: React.FC = () => {
  const [downloadOptions, setDownloadOptions] = useState<
    Array<{ platform: string; architecture: string; url: string }>
  >([]);
  const [defaultDownload, setDefaultDownload] = useState<{
    platform: string;
    url: string;
  } | null>(null);

  useEffect(() => {
    const options: Array<{
      platform: string;
      architecture: string;
      url: string;
    }> = [];
    Object.entries(binaries).forEach(([platform, archs]) => {
      Object.entries(archs).forEach(([architecture, url]) => {
        options.push({ platform, architecture, url });
      });
    });
    setDownloadOptions(options);

    // Detect platform and architecture
    const detectPlatformAndDownload = async () => {
      let platform = "Unknown";
      let architecture = "Unknown";

      if ((navigator as any).userAgentData) {
        const data = await (
          navigator as any
        ).userAgentData.getHighEntropyValues(["platform", "architecture"]);
        platform = data.platform || "Unknown";
        architecture = data.architecture || "Unknown";
      } else {
        const userAgent = navigator.userAgent.toLowerCase();
        if (userAgent.includes("win")) platform = "Windows";
        if (userAgent.includes("mac")) platform = "macOS";
        if (userAgent.includes("arm")) architecture = "arm";
        else if (userAgent.includes("x86") || userAgent.includes("i386"))
          architecture = "x86";
      }

      const url = binaries[platform]?.[architecture];
      if (url) {
        setDefaultDownload({ platform, url });
        window.location.href = url; // Trigger the download automatically
      }
    };

    detectPlatformAndDownload();
  }, []);

  const steps = (
    <Grid container spacing={4}>
      <Grid item xs={12} md={4}>
        <Typography variant="h6" textAlign="center" color="primary">
          Step 1. Open
        </Typography>
        <Typography variant="body2" textAlign="center">
          Open the Trusty.dmg file from Chrome Downloads at the top right
          corner.
        </Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="h6" textAlign="center" color="secondary">
          Step 2. Install
        </Typography>
        <Typography variant="body2" textAlign="center">
          Drag and drop Trusty to the Applications folder. You might be asked
          for your admin password.
        </Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="h6" textAlign="center" color="error">
          Step 3. Launch
        </Typography>
        <Typography variant="body2" textAlign="center">
          Open the Launchpad, type Trusty, and launch the app.
        </Typography>
      </Grid>
    </Grid>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "radial-gradient(65.22% 100% at 50% 0%, rgba(238, 171, 0, 0.4) 0%, rgba(238, 171, 0, 0) 100%)",
      }}
    >
      <Container maxWidth="lg">
        <Box
          mb={3}
          display="flex"
          justifyContent="center"
          sx={{
            textAlign: "center",
          }}
        >
          <Wordmark />
        </Box>

        <Paper
          elevation={3}
          sx={{ padding: 4, borderRadius: 2, marginBottom: 4 }}
        >
          <Box>
            <Typography variant="h4" textAlign="center" gutterBottom>
              Thanks for downloading! Just a few steps left
            </Typography>
            <Typography variant="body1" textAlign="center">
              Your download will begin automatically. If it didn’t start,{" "}
              <a
                href={defaultDownload?.url || "#"}
                style={{ color: "#1976d2", textDecoration: "none" }}
              >
                download Trusty manually
              </a>
              .
            </Typography>
          </Box>
          <Box marginTop={6}>{steps}</Box>
          <Box marginTop={6}>
            <Typography variant="h6" textAlign="center" gutterBottom>
              Available Downloads
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">Platform</TableCell>
                    <TableCell align="center">Architecture</TableCell>
                    <TableCell align="center">Download Link</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {downloadOptions.map((option, index) => (
                    <TableRow key={index}>
                      <TableCell align="center">
                        {platformIcons[option.platform] || option.platform}
                      </TableCell>
                      <TableCell align="center">
                        {option.architecture}
                      </TableCell>
                      <TableCell align="center">
                        <StyledButton
                          href={option.url}
                          rel="noopener noreferrer"
                        >
                          Download
                        </StyledButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default App;
